import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';
import { legalEntityKeys } from './useLegalEntities';

const subcontractorKeys = {
  all: ['subcontractors'],
  lists: () => [...subcontractorKeys.all, 'list'],
  details: () => [...subcontractorKeys.all, 'detail'],
  detail: (id) => [...subcontractorKeys.details(), id],
};

const extractSubcontractorItems = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== 'object') {
    return [];
  }

  if (Array.isArray(payload.results)) {
    return payload.results;
  }

  if (Array.isArray(payload.items)) {
    return payload.items;
  }

  if (Array.isArray(payload.data)) {
    return payload.data;
  }

  return [];
};

const resolveNextPage = (payload, currentPage) => {
  if (!payload || Array.isArray(payload)) {
    return null;
  }

  const { next_page: nextPage, page, pages, total_pages: totalPages, total, per_page: perPage } = payload;

  if (typeof nextPage === 'number' && nextPage > currentPage) {
    return nextPage;
  }

  if (typeof page === 'number' && typeof pages === 'number' && page < pages) {
    return currentPage + 1;
  }

  if (typeof totalPages === 'number' && currentPage < totalPages) {
    return currentPage + 1;
  }

  if (typeof total === 'number' && typeof perPage === 'number' && currentPage * perPage < total) {
    return currentPage + 1;
  }

  return null;
};

const fetchAllSubcontractors = async ({ perPage = 100 } = {}) => {
  let currentPage = 1;
  const items = [];
  let iterationGuard = 0;
  const MAX_ITERATIONS = 50;

  do {
    const response = await apiClient.get('legal_entities/subcontractors', {
      params: {
        page: currentPage,
        per_page: perPage,
      },
    });

    const payload = response.data;
    const pageItems = extractSubcontractorItems(payload);

    if (!Array.isArray(pageItems)) {
      console.warn('Respuesta inesperada al consultar subcontratistas:', payload);
      break;
    }

    items.push(...pageItems);

    const nextPage = resolveNextPage(payload, currentPage);
    if (!nextPage || iterationGuard >= MAX_ITERATIONS) {
      break;
    }

    currentPage = nextPage;
    iterationGuard += 1;
  } while (true);

  return items;
};

/**
 * Hook para obtener todos los subcontratistas
 */
export const useSubcontractors = () => {
  return useQuery({
    queryKey: subcontractorKeys.lists(),
    queryFn: () => fetchAllSubcontractors(),
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para obtener un subcontratista específico
 * @param {string} id - UUID del subcontratista
 * @param {Object} options - Opciones adicionales para la query
 */
export const useSubcontractor = (id, options = {}) => {
  return useQuery({
    queryKey: subcontractorKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`legal_entities/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para crear un nuevo subcontratista
 */
const sanitizePayload = (data) => {
  
  const sanitized = {};

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined) return;

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') return;
      sanitized[key] = trimmed;
      return;
    }

    sanitized[key] = value;
  });

  if (data.uuid_district) {
    sanitized.uuid_district = data.uuid_district.trim();
  } else {
    sanitized.uuid_district = null;
  }

  return sanitized;
};

export const useCreateSubcontractor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (subcontractorData) => {
      // Asegurar que sea subcontractor y provider
      const normalizedName =
        subcontractorData.company?.trim() || subcontractorData.legal_business_name?.trim() || '';

      const dataWithFlags = {
        ...subcontractorData,
        name: normalizedName,
        is_subcontractor: true,
        is_provider: true,
        entity_type: 'PROVIDER',
      };

      const payload = sanitizePayload(dataWithFlags);

      const response = await apiClient.post('legal_entities/beneficiaries', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subcontractorKeys.lists() });
      queryClient.invalidateQueries({ queryKey: legalEntityKeys.lists() });
      queryClient.invalidateQueries({ queryKey: legalEntityKeys.collecting() });
    },
    onError: (error) => {
      console.error('Error creating subcontractor:', error);
    },
  });
};

/**
 * Hook para actualizar un subcontractista existente
 */
export const useUpdateSubcontractor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (subcontractorData) => {
      const payload = {
        ...subcontractorData,
        is_provider: subcontractorData.is_provider ?? true,
        is_subcontractor: subcontractorData.is_subcontractor ?? true,
      };
      const response = await apiClient.put(
        `legal_entities/${subcontractorData.uuid}`,
        payload
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(subcontractorKeys.detail(data.uuid), data);
      queryClient.invalidateQueries({ queryKey: subcontractorKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating subcontractor:', error);
    },
  });
};

/**
 * Hook para eliminar un subcontratista
 */
export const useDeleteSubcontractor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`legal_entities/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: subcontractorKeys.lists() });
      queryClient.removeQueries({ queryKey: subcontractorKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting subcontractor:', error);
    },
  });
};
